import { nextTick } from 'vue';
import content_method from '../mixins/content_method';
import Content from '../classes/content.js';
import { graphemeCount, sliceGraphemes } from '../text_segment.js';

export default {
  mixins: [content_method],
  methods: {
    /**
     * cキー, xキー押下時の処理
     * @param {KeyboardEvent} event
     * @return {Array} コピー, カットしたテキストのtext、type(togaki、serifu、hashira)を要素毎に配列化したもの
     */
    async cxKeyAction(event) {
      // Metaキー(ctrlかcommandのどちらか)が押されている場合コピーする
      if ((event.metaKey && event.ctrlKey) || (!event.metaKey && !event.ctrlKey) || !this.selectTo.contentID) return;
      event.preventDefault();
      const fromContent = this.contentMap.get(this.selectFrom.contentID);
      let toContent = null;
      let currentContent = fromContent;
      let isNext = false;
      while (!toContent && currentContent) {
        if (currentContent.uuid == this.selectTo.contentID) {
          toContent = currentContent;
          isNext = true;
        }
        currentContent = currentContent.next;
      }
      currentContent = fromContent;
      while (!toContent && currentContent) {
        if (currentContent.uuid == this.selectTo.contentID) {
          toContent = currentContent;
        }
        currentContent = currentContent.previous;
      }
      if (!toContent) return;
      let copyText = '';
      if (fromContent == toContent) {
        if (this.selectFrom.wordIndex > this.selectTo.wordIndex) {
          copyText = sliceGraphemes(fromContent.updatingbody, this.selectTo.wordIndex, this.selectFrom.wordIndex + 1);
        } else {
          copyText = sliceGraphemes(fromContent.updatingbody, this.selectFrom.wordIndex, this.selectTo.wordIndex + 1);
        }
      } else {
        currentContent = isNext ? fromContent : toContent;
        copyText = sliceGraphemes(currentContent.body, currentContent.selectFrom, currentContent.selectTo + 1);
        if (graphemeCount(copyText) == currentContent.graphemeLength && currentContent.tag_type == 'hashira') {
          copyText = '◯' + copyText;
        }
        currentContent = currentContent.next;
        while (currentContent != (isNext ? toContent : fromContent)) {
          copyText = copyText + '\n' + (currentContent.tag_type == 'hashira' ? '◯' : '') + currentContent.body;
          currentContent = currentContent.next;
        }
        copyText =
          copyText +
          '\n' +
          (currentContent.tag_type == 'hashira' ? '◯' : '') +
          sliceGraphemes(currentContent.body, currentContent.selectFrom, currentContent.selectTo + 1);
      }
      // xKey の場合のみ、選択範囲の削除を行う
      if (event.key == 'x') {
        this.addConfirmEvent();
        this.pushUndo();
        this.resetRedoRecords();
        // 選択状態のリセットとカーソル再設定はメソッド内で行うため、awaitして完了を待つ
        await this.deleteSelectedWordsAndSetCaret();
      }
      navigator.clipboard.writeText(copyText);
    },
    /**
     * vキー押下時の処理
     * @param {KeyboardEvent} event
     */
    async vKeyAction(event) {
      if ((event.metaKey && event.ctrlKey) || (!event.metaKey && !event.ctrlKey)) return;
      event.preventDefault();
      navigator.clipboard.readText().then(async (clipText) => {
        this.addConfirmEvent();
        this.pushUndo();
        this.resetRedoRecords();
        // Tab文字をスペース4文字に変換する（Tab文字がそのままコンテンツに入ると表示が崩れるため）
        clipText = clipText.replace(/\t/g, '    ');
        // Windows系クリップボード(\r\n)対応: split前に一括で\rを除去
        clipText = clipText.replace(/\r/g, '');
        let pasteTextArray = clipText.split('\n');
        await this.deleteSelectedWordsAndSetCaret();
        const selection = window.getSelection(); // caret位置取得用
        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;
        const offset = selection.focusOffset > 0 ? 1 : 0;
        let targetDivIndex = Number(targetDiv.dataset.index) + offset;
        const parentDiv = targetDiv.parentNode;
        const targetContent = this.contentMap.get(parentDiv.dataset.contentid);
        let firstHarfText = sliceGraphemes(targetContent.updatingbody, 0, targetDivIndex);
        let secondHarfText = sliceGraphemes(targetContent.updatingbody, targetDivIndex);
        let currentContent = targetContent;
        let lastContent = targetContent.next;
        let firstPasteText = pasteTextArray[0];
        if (targetDivIndex == 0 && firstPasteText.startsWith('◯')) {
          currentContent.updateBody(
            sliceGraphemes(currentContent.updatingbody, 0, targetDivIndex) + firstPasteText.slice(1),
          );
          currentContent.tag_type = 'hashira';
        } else {
          currentContent.updateBody(sliceGraphemes(currentContent.updatingbody, 0, targetDivIndex) + firstPasteText);
        }
        for (var i = 1; i < pasteTextArray.length; i++) {
          let pasteText = pasteTextArray[i];
          let tagType = 'togaki';
          if (pasteText.includes('「')) {
            tagType = 'serifu';
          } else if (pasteText.startsWith('◯')) {
            tagType = 'hashira';
            pasteText = pasteText.slice(1);
          }
          let previousContent = currentContent;
          currentContent = new Content({
            updatingbody: pasteText,
            body: pasteText,
            tag_type: tagType,
            previous: currentContent,
          });
          this.contents.push(currentContent);
          previousContent.next = currentContent;
        }
        currentContent.updateBody(currentContent.updatingbody + secondHarfText);
        if (lastContent) {
          currentContent.next = lastContent;
          lastContent.previous = currentContent;
        }
        nextTick(() => {
          const range = document.createRange();
          let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${currentContent.uuid}"]`);
          let nextFocusDiv = null;
          let lastSentence = pasteTextArray[pasteTextArray.length - 1];
          let lastSentenceLength = graphemeCount(lastSentence) - (lastSentence.startsWith('◯') ? 1 : 0);
          nextFocusContentElements.forEach((ele) => {
            let selectorArray = ele.querySelectorAll(
              `[data-index="${Math.max(
                0,
                (currentContent == targetContent ? graphemeCount(firstHarfText) : 0) + lastSentenceLength - 1,
              )}"]`,
            );
            if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
          });
          range.setStart(nextFocusDiv, lastSentenceLength > 0 ? 1 : 0);
          range.setEnd(nextFocusDiv, lastSentenceLength > 0 ? 1 : 0);
          selection.removeAllRanges();
          selection.addRange(range);
        });
      });
    },
  },
};
